import { motion } from "motion/react";
import { MessageCircle } from "lucide-react";
import { SectionLabel } from "@/components/common/SectionLabel";
import { ANIMATION_VARIANTS } from "@/constants/animations";
import { COMPANY_INFO, CONTACT_INFO } from "@/constants/company";
import heroBg from "@/assets/images/Fondo-inicio.webp";

interface HeroSectionProps {
  onNavigate: (id: string) => void;
}

export function HeroSection({ onNavigate }: HeroSectionProps) {
  return (
    <section
      id="inicio"
      className="relative pt-28 h-screen min-h-[800px] flex items-center overflow-hidden"
    >
      {/* Imagen de fondo con overlay */}
      <motion.div
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.2, ease: "easeOut" }}
        className="absolute inset-0 z-0"
      >
        <img
          src={heroBg}
          alt="Infraestructura Industrial"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A2540]/10 mix-blend-overlay z-10" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/85 to-[#e4f6f5]/80 z-20" />
      </motion.div>

      {/* Línea decorativa */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{
          duration: 1.4,
          delay: 0.8,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="absolute left-0 top-1/4 w-1 h-40 bg-gradient-to-b from-[#39B5BF] to-transparent origin-top z-30"
      />

      <div className="relative z-30 max-w-7xl mx-auto px-6 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <SectionLabel text="Soluciones Técnicas Integrales" />
        </motion.div>

        <motion.h1
          variants={ANIMATION_VARIANTS.fadeUp}
          custom={0.2}
          initial="hidden"
          animate="visible"
          className="text-4xl md:text-6xl font-bold text-[#0A2540] leading-[1.2] max-w-5xl tracking-tight"
        >
          Infraestructura Inteligente:{" "}
          <br className="hidden md:block" />
          <span className="text-[#39B5BF]">
            Su aliado integral en soluciones técnicas y tecnológicas
          </span>
        </motion.h1>

        <motion.p
          variants={ANIMATION_VARIANTS.fadeUp}
          custom={0.4}
          initial="hidden"
          animate="visible"
          className="mt-8 text-[16px] md:text-[18px] text-[#0A2540]/80 max-w-3xl font-normal leading-[1.7]"
        >
          {COMPANY_INFO.description}
        </motion.p>

        <motion.div
          variants={ANIMATION_VARIANTS.fadeUp}
          custom={0.6}
          initial="hidden"
          animate="visible"
          className="mt-12 flex flex-wrap gap-4"
        >
          <motion.button
            onClick={() => onNavigate("servicios")}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="bg-[#1A3F75] text-white px-8 py-4 rounded-lg font-bold tracking-wide hover:bg-[#0A2540] transition-all hover:shadow-xl shadow-lg"
          >
            Explorar Soluciones Integrales
          </motion.button>
          <motion.button
            onClick={() => onNavigate("contacto")}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="bg-[#25D366] text-white px-8 py-4 rounded-lg font-bold tracking-wide hover:bg-[#20B45B] transition-all hover:shadow-xl shadow-lg flex items-center gap-2"
          >
            <MessageCircle className="w-5 h-5" />
            Contáctenos
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}